import { Component, EventEmitter, Input, OnDestroy, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { Observable, Subject, combineLatest, of } from 'rxjs';
import {
  distinctUntilChanged,
  filter,
  map,
  skip,
  take,
  takeUntil,
  withLatestFrom
} from 'rxjs/operators';
import { selectIsJointAccount, selectTemplatesList } from '../../../account-info-header/reducers';
import { ModifiedPayeeNames } from '../../../modified-first-parties/models/FirstParties';
import { TextInputModel } from '../../../shared/components/controls/text-input-control/TextInputModel';
import { ValidateInputService } from '../../../shared/services/validate-input.service';
import { Constants, isoInputFieldErrorMap, isoInputZipErrorMap, taxPayerNameErrorMap } from '../../../shared/utilities/Constants';
import { isoFieldsAllowedChars, zipCodePattern } from '../../../shared/utilities/regex-patterns';
import { TransactionFormActions } from '../../actions';
import {
  OptionDropdown,
  OptionDropdownSelect,
  SelectControl,
} from '../../models/OptionDropdownSelect';
import { Country } from '../../models/shared/Country';
import { ReceivingAccountHolder, ReceivingAccountHolderIso, WireInfo } from '../../models/wire/WireInfo';
import { DraftCommonWire } from '../../models/wire/WireTemplatesResponse';
import { selectIsFedWire,  selectUseStandingAuth, selectUseTemplate, selectWireAbaCode, selectWireDetailsError, selectWireDetailsWarning, selectTemplateId, selectWireIntermediarySelection, selectWireInfo, selectIsFromEdit} from '../../reducers';
import { selectStandingAuthWireInfo, selectWireDetailsErrorStandingAuth, selectWireDetailsWarningStandingAuth} from '../../../standing-authorizations/reducers';
import { selectStandingAuthWireIntermediarySelection } from '../../../standing-authorizations/reducers';
import { createControl } from '../../utilities/formUtilities';
import { ShowHideTextControl } from '../../models/form/FormControlAlternatingTextLink';
import { FormatIsoAddressFieldsService } from '../../../shared/services/format-isoAddressFields.service';
import { CountryDescriptionFromCode } from '../../../shared/utilities/CountryDescriptionFromCode';

@Component({
  standalone: false,
  selector: 'mm-wire-iso-receiving-account-holder',
  templateUrl: './wire-iso-receiving-account-holder.component.html',
  styleUrls: ['./wire-iso-receiving-account-holder.component.scss'],
})
export class WireIsoReceivingAccountHolderComponent implements OnInit, OnDestroy {
  @Input() parentForm: UntypedFormGroup;
  @Input() receivingAccountHolder: ReceivingAccountHolderIso;
  @Input() firstParty$: Observable<'firstParty' | 'thirdParty' | ''>;
  @Input() countryList$: Observable<Country[]>;
  @Input() useSloa: boolean;
  @Input() missingThirdPartyAddress: boolean;
  @Input() firstPartyPayees: ModifiedPayeeNames[];
  @Input() payeeName$: Observable<string | undefined> = of('');
  @Input() modifiedPayeeCheckbox$: Observable<boolean | undefined> = of(false);
  @Input() isoModifiedPayeeName$: Observable<string | undefined> = of('');
  @Input() fromTemplateStartTransaction: boolean;
  @Input() isFromEditReview: boolean;
  @Input() isFromCreateTransaction: boolean;
  irsAbaPlaceholder: string = 'Last, First or business name';
  useSloaFirstPartyAddress: string;
  wireInputFields = new EventEmitter<string>();
  warningDescription: string = Constants.recipientAddressWireBannerDescription;
  nonIsoCompliantErrorHeader: string = Constants.isoWireTemplateErrorBannerHeader;
  nonIsoCompliantErrorDescription: string = Constants.isoWireTemplateErrorBannerDescriptionRecipient;
  nonIsoCompliantErrorAddressDetails: string;
  wireInfo$: Observable<WireInfo>;
  modifiedPayeeTooltipVerbiage = Constants.modifiedPayeeTooltipVerbiage;
  jointRecipientBannerText: string = Constants.jointPayeeBannerText;
  warningAbaDescription: string = Constants.receivingAbaAccountNumberWarningDescription;
  firstPartyBoolean$: Observable<boolean>;
  isJointAccount: boolean;
  useModifiedPayeeChecked: boolean;
  delimiter: string;
  destroy$ = new Subject<void>();
  hideAddressFields: string | null;
  showIntermediaryFields$: Observable<boolean> | undefined;
  wireIntermediary$: Observable<'yes' | 'no' | ''>;
  isFedWire$: Observable<boolean>;
  useSloa$: Observable<boolean>;
  useTemplate$: Observable<boolean>;
  wireAbaCode$: Observable<string>;
  wireDetailsError$: Observable<string>;
  wireDetailsWarning$: Observable<string>;
  wireAccountNumber: string;
  isFromTemplateAndNonIsoCompliant: boolean = false;
  isInternationalForUseSloa: string;
  isISOCompliant: boolean;
  isNonIsoFirstPartyTemplate: boolean = false;
  nonIsoFirstPartyTemplateRecAccountHolder: ReceivingAccountHolder;
  isFirstParty: boolean;
  isIrsAba: boolean;
  readonly controlNames = {
    name1: 'name',
    name2: 'name2',
    nameCont: 'receivingAccountHolderNameCont',
    address: 'receivingAccountHolderAddress1',
    addressCont: 'receivingAccountHolderAddressCont',
    streetNumber: 'receivingAccountHolderStreetNumber',
    streetName: 'receivingAccountHolderStreetName',
    apartmentSuiteEtc: 'receivingAccountHolderApartmentSuiteEtc',
    country: 'receivingAccountHolderCountry',
    receivingBankCity: 'receivingAccountHolderCity',
    receivingBankState: 'receivingAccountHolderState',
    zipCode: 'receivingAccountHolderZip',
    department: 'receivingAccountHolderDepartment',
    buildingName: 'receivingAccountHolderBuildingName',
    floor: 'receivingAccountHolderFloor',
    townLocationName: 'receivingAccountHolderTownLocationName',
    district: 'receivingAccountHolderDistrict'
  };
  countryListDropdown: SelectControl = {
    title: 'Country',
    controlName: this.controlNames.country,
    options: [],
    validationMessage: 'Country selection is required.',
  };

  intermediaryShowHideLinkModel: ShowHideTextControl = {
    controlName: 'addressSelection',
    text1: {
      key: 'Show additional address fields',
      value: 'no'
    },
    text2: {
      key: 'Hide additional address fields',
      value: 'yes'
    }
  }

  standardMaxLength = 140;
  maxLength35 = 35;
  maxLength16 = 16;
  maxLength70 = 70;

  name1IsoModel: TextInputModel = {
    controlName: this.controlNames.name1,
    label: 'Recipient name',
    hasGreyText: true,
    greyText: '(person/business)',
    htmlMaxLength: this.standardMaxLength,
  };
  taxPayerNameModel: TextInputModel = {
    controlName: this.controlNames.name1,
    label: 'Taxpayer name',
    hasGreyText: false,
    htmlMaxLength: this.standardMaxLength
  };
  name2IsoModel: TextInputModel = {
    controlName: this.controlNames.name2,
    label: 'Recipient name',
    hasGreyText: true,
    greyText: '(person/business)',
    htmlMaxLength: this.standardMaxLength
  };

  nameContModel: TextInputModel = {
    controlName: this.controlNames.nameCont,
    label: 'Recipient name continued',
    hasGreyText: true,
    greyText: Constants.optionalDisplayText,
    htmlMaxLength: 40,
  };

  addressModel: TextInputModel = {
    controlName: this.controlNames.address,
    label: 'Address',
    hasGreyText: false,
    htmlMaxLength: 40,
    secondLineGreyText: ''
  };

  addressContModel: TextInputModel = {
    controlName: this.controlNames.addressCont,
    label: 'Address continued',
    hasGreyText: true,
    greyText: Constants.optionalDisplayText,
    htmlMaxLength: 40,
  };

  streetNumberModel: TextInputModel = {
    controlName: this.controlNames.streetNumber,
    label: 'Street number',
    hasGreyText: true,
    greyText: '(recommended)',
    htmlMaxLength: this.maxLength16
  };

  streetNameModel: TextInputModel = {
    controlName: this.controlNames.streetName,
    label: 'Street name',
    hasGreyText: true,
    greyText: '(recommended)',
    htmlMaxLength: this.maxLength70
  };

  apartmentSuiteModel: TextInputModel = {
    controlName: this.controlNames.apartmentSuiteEtc,
    label: 'Apartment, suite, etc.',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: this.maxLength70
  };

  cityModel: TextInputModel = {
    controlName: this.controlNames.receivingBankCity,
    label: 'City',
    hasGreyText: false,
    htmlMaxLength: this.maxLength35
  };

  stateModel: TextInputModel = {
    controlName: this.controlNames.receivingBankState,
    label: 'State',
    hasGreyText: false,
    htmlMaxLength: this.maxLength35
  };

  zipModel: TextInputModel = {
    controlName: this.controlNames.zipCode,
    label: 'ZIP code',
    hasGreyText: true,
    greyText: '(recommended)',
    htmlMaxLength: this.maxLength16
  };

  // International Models

  intlApartmentSuiteModel: TextInputModel = {
    controlName: this.controlNames.apartmentSuiteEtc,
    label: 'Flat, suite, etc.',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: this.maxLength70
  };

  intlZipModel: TextInputModel = {
    controlName: this.controlNames.zipCode,
    label: 'Postal code',
    hasGreyText: true,
    greyText: '(recommended)',
    htmlMaxLength: this.maxLength16
  };

  intlStateModel: TextInputModel = {
    controlName: this.controlNames.receivingBankState,
    label: 'State/Province/Region',
    hasGreyText: true,
    secondLineGreyText: '(optional)',
    htmlMaxLength: this.maxLength35,
    hasIconTooltip: true,
    tooltip: {
      position: 'right',
      containsLink: false,
      content: Constants.intlStateToolTip
    }
  };

  districtModel: TextInputModel = {
    controlName: this.controlNames.district,
    label: 'District',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: this.maxLength35,
    hasIconTooltip: true,
    tooltip: {
      position: 'right',
      containsLink: false,
      content: Constants.districtTooltip
    }
  };

  intlCityModel: TextInputModel = {
    controlName: this.controlNames.receivingBankCity,
    label: 'City/Town/Locality',
    hasGreyText: false,
    htmlMaxLength: this.maxLength35
  };

  townLocationNameModel: TextInputModel = {
    controlName: this.controlNames.townLocationName,
    label: 'Sub-locality',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: this.maxLength35,
    hasIconTooltip: true,
    tooltip: {
      position: 'right',
      containsLink: false,
      content: Constants.intlSublocalityToolTip
    }
  };

  departmentModel: TextInputModel = {
    controlName: this.controlNames.department,
    label: 'Department',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: this.maxLength70,
    hasIconTooltip: true,
    tooltip: {
      position: 'right',
      containsLink: false,
      content: Constants.departmentTooltip
    }
  };

  buildingNameModel: TextInputModel = {
    controlName: this.controlNames.buildingName,
    label: 'Building name',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: this.maxLength35
  };

  floorModel: TextInputModel = {
    controlName: this.controlNames.floor,
    label: 'Floor',
    hasGreyText: true,
    greyText: '(optional)',
    htmlMaxLength: this.maxLength70
  };

  
  firstPartyPayeesSelectOptions: SelectControl = {
    title: 'Recipient name',
    controlName: 'payeeName',
    options: [],
    validationMessage: 'Payee name is required',
    showOptionalLabel: false
  };
  templates: DraftCommonWire[];
  templateId: string;

  constructor(
    private fb: UntypedFormBuilder,
    public validationService: ValidateInputService,
    private continueClickInvalid$: Actions,
    private store: Store,
    private formatIsoAddressFieldsService: FormatIsoAddressFieldsService
  ) { }

  ngOnInit(): void {
    this.isISOCompliant = this.parentForm?.value?.isISOCompliant;
    this.store.select(selectIsJointAccount).pipe(takeUntil(this.destroy$)).subscribe( isJoint => {
      this.isJointAccount = isJoint;
      this.delimiter = isJoint ? ' & ' : ' ';
    });
    if (this.isFromCreateTransaction) {
      this.wireIntermediary$ = this.store.select(selectWireIntermediarySelection);
      this.wireInfo$ = this.store.select(selectWireInfo);
      this.wireDetailsError$ = this.store.select(selectWireDetailsError);
      this.wireDetailsWarning$ = this.store.select(selectWireDetailsWarning);
    }
    else {
      this.wireIntermediary$ = this.store.select(selectStandingAuthWireIntermediarySelection);
      this.wireInfo$ = this.store.select(selectStandingAuthWireInfo);
      this.wireDetailsError$ = this.store.select(selectWireDetailsErrorStandingAuth);
      this.wireDetailsWarning$ = this.store.select(selectWireDetailsWarningStandingAuth);
    }

    if ((this.department?.value && this.department?.value !== "") || (this.buildingName?.value && this.buildingName?.value !== "") || (this.floor?.value && this.floor?.value !== "")) {
      this.hideAddressFields = "no";
    } else {
      this.hideAddressFields = "yes";
    }
    if(this.useSloa) {
      this.hideAddressFields = "no";
    }

    this.showIntermediaryFields$ = this.wireIntermediary$.pipe(map((selection) => selection === 'yes'));
    this.isFedWire$ = this.store.select(selectIsFedWire);
    this.useSloa$ = this.store.select(selectUseStandingAuth);
    this.useTemplate$ = this.store.select(selectUseTemplate);
    this.wireAbaCode$ = this.store.select(selectWireAbaCode);
    if (this.fromTemplateStartTransaction || this.isFromEditReview) {
      this.store.select(selectTemplatesList).pipe(takeUntil(this.destroy$)).subscribe( templates => this.templates = templates );
      this.store.select(selectTemplateId).pipe(takeUntil(this.destroy$)).subscribe( id => this.templateId = id ?? '' );
    }
    if(this.isFromCreateTransaction) {
      this.store.select(selectWireInfo).pipe(takeUntil(this.destroy$)).subscribe(wireInfo => {
        this.isIrsAba = wireInfo.isIrsAba;
      });
    } else {
      this.store.select(selectStandingAuthWireInfo).pipe(takeUntil(this.destroy$)).subscribe(wireInfo => {
        this.isIrsAba = wireInfo.isIrsAba;
      });
    }

    this.firstPartyBoolean$ = this.firstParty$.pipe(
      map((controlValue) => controlValue === 'firstParty')
    );


    this.countryList$?.pipe(takeUntil(this.destroy$)).subscribe((countries) => {
      this.countryListDropdown.options = countries.map((country) => {
        return { value: country.code, name: country.description };
      });
    });
    this.firstPartyBoolean$.pipe(take(1)).subscribe((firstParty) => {
      let accountHolderName1 = '';
      let accountHolderStreetNumber = '';
      let accountHolderStreetName = '';
      let accountHolderApartmentSuiteEtc = '';
      let country = 'US';
      let name2 = '';
      let nameCont = '';
      let address = '';
      let addressCont = '';
      let receivingBankCity = '';
      let receivingBankState = '';
      let zipCode = '';
      let department = '';
      let buildingName = '';
      let floor = '';
      let townLocationName = '';
      let district = '';
      this.isFirstParty = firstParty;

      if (firstParty) {
        accountHolderName1 = this.receivingAccountHolder.name;
        accountHolderStreetNumber = this.receivingAccountHolder.accountHolderStreetNumber;
        accountHolderStreetName = this.receivingAccountHolder.accountHolderStreetName;
        accountHolderApartmentSuiteEtc = this.receivingAccountHolder.accountHolderApartmentSuiteEtc;
        country = this.receivingAccountHolder.countryCode;
        receivingBankCity = this.receivingAccountHolder.receivingBankCity;
        receivingBankState = this.receivingAccountHolder.receivingBankState;
        zipCode = this.receivingAccountHolder.zipCode;
        department = this.receivingAccountHolder.department;
        buildingName = this.receivingAccountHolder.buildingName;
        floor = this.receivingAccountHolder.floor;
        townLocationName = this.receivingAccountHolder.townLocationName;
        district = this.receivingAccountHolder.district;


        if (this.allowModifiedPayee){
          createControl(this.parentForm, 'payeeName', this.getDefaultPayee(), [Validators.required], undefined, undefined,
            this.isFromEditReview && !this.fromTemplateStartTransaction ? false : true);
          createControl(this.parentForm, 'modifiedPayeeCheckbox', false);
          this.constructFirstPartyPayeeOptions();
        }
      }
      if(this.useSloa) {
        this.getUseSloaFirstPartyAddress();
        this.isInternationalForUseSloa = this.parentForm?.value?.receivingAccountHolderCountry;
      }

      createControl(
        this.parentForm,
        this.controlNames.name1,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderName : accountHolderName1,
        [
          Validators.required,
          Validators.pattern(isoFieldsAllowedChars),
        ],
        undefined,
        this.useSloa
      );
      createControl(
        this.parentForm,
        this.controlNames.streetNumber,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderStreetNumber : accountHolderStreetNumber,
        [
          Validators.pattern(isoFieldsAllowedChars),
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress

      );
      createControl(
        this.parentForm,
        this.controlNames.streetName,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderStreetName : accountHolderStreetName,
        [
          Validators.pattern(isoFieldsAllowedChars),
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.apartmentSuiteEtc,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderApartmentSuiteEtc : accountHolderApartmentSuiteEtc,
        [
          Validators.pattern(isoFieldsAllowedChars),
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.country,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderCountry : country,
        [Validators.required],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.receivingBankCity,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderCity : receivingBankCity,
        this.receivingAccountHolderCityStateValidators(firstParty),
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.receivingBankState,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderState : receivingBankState,
        this.receivingAccountHolderCityStateValidators(firstParty),
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.zipCode,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderZip : zipCode,
        [
          Validators.pattern(zipCodePattern),
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.department,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderDepartment : department,
        [
          Validators.pattern(isoFieldsAllowedChars),
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.buildingName,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderBuildingName : buildingName,
        [
          Validators.pattern(isoFieldsAllowedChars),
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.floor,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderFloor : floor,
        [
          Validators.pattern(isoFieldsAllowedChars),
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.townLocationName,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderTownLocationName : townLocationName,
        [
          Validators.pattern(isoFieldsAllowedChars)
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.district,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderDistrict : district,
        [
          Validators.pattern(isoFieldsAllowedChars)
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.name2,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderName1 : name2,
        [
          Validators.pattern(isoFieldsAllowedChars),
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.nameCont,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderName2 : nameCont,
        [
          Validators.pattern(isoFieldsAllowedChars),
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.address,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderAddress1 : address,
        [
          Validators.pattern(isoFieldsAllowedChars),
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
      createControl(
        this.parentForm,
        this.controlNames.addressCont,
        this.useSloa ? this.parentForm?.value?.receivingAccountHolderAddress2 : addressCont,
        [
          Validators.pattern(isoFieldsAllowedChars),
        ],
        undefined,
        this.useSloa && !this.missingThirdPartyAddress
      );
    });

    this.firstPartyBoolean$
      .pipe(
        takeUntil(this.destroy$),
        skip(1),
        distinctUntilChanged()
      )
      .subscribe((firstParty) => {
        this.isFirstParty = firstParty;
        this.resetReceivingAccountHolderControls(firstParty);
      });

      this.parentForm.controls.receivingAccountHolderCountry?.valueChanges
      .pipe(
        takeUntil(this.destroy$),
        distinctUntilChanged()
      )
      .subscribe((country) => {
        this.resetInternationalFields(country);
        this.resetValidators(this.isFirstParty);
      });


    this.payeeName$
      .pipe(
        takeUntil(this.destroy$),
        filter((payeeName) => this.allowModifiedPayee && !!payeeName)
      )
      .subscribe((payeeName) => {
        this.setReceivingAccountNames(payeeName ?? '');
      });

    this.modifiedPayeeCheckbox$
      .pipe(
        takeUntil(this.destroy$),
        withLatestFrom(
          this.payeeName$,
          this.firstPartyBoolean$
        ),
        filter(([_x, _y, firstParty]) => firstParty && this.allowModifiedPayee)
      )
      .subscribe(([checked, selectedPayeeName]) => {
        this.useModifiedPayeeChecked = checked ?? false;
        if (checked) {
          this.payeeName.disable();
        } else {
          this.payeeName.enable();
          this.setReceivingAccountNames(selectedPayeeName ?? '');
        }
      });

    combineLatest([
      this.isoModifiedPayeeName$,
    ])
      .pipe(
        takeUntil(this.destroy$),
        filter(
          ([mp1]) =>
            this.allowModifiedPayee && this.useModifiedPayeeChecked && !!mp1
        )
      )
      .subscribe(([mp1]) => {
        if (!!mp1?.trim()) {
          this.name1?.setValue(mp1.trim().toUpperCase());
        }
      });

    //TODO: remove after a more generic solution for scrolling to validation messages is implemented
    //added temporarily for green SLOAs with missing address info
    this.continueClickInvalid$.pipe(
      ofType(TransactionFormActions.continueClickInvalid),
      takeUntil(this.destroy$)
    )
      .subscribe(() => {
        if (this.missingThirdPartyAddress && this.streetNumberError) {
          const elem = document.getElementById(this.controlNames.streetNumber);
          setTimeout(() => {
            elem?.focus();
            elem?.scrollIntoView({ behavior: 'smooth', block: 'end', inline: 'start' });
          });
        }
      });

    this.resetValidators(this.isFirstParty);

    if (this.fromTemplateStartTransaction || this.isFromEditReview) {
      const templateInContext = this.templates.find(template => template.draftId === this.templateId);
      if (templateInContext && !templateInContext.draftObject.isIsoCompliant) {
        this.isFromTemplateAndNonIsoCompliant = true;
        this.name1?.setValue(templateInContext.draftObject.beneficiary?.namePartOne + ' '
          + templateInContext.draftObject.beneficiary?.namePartTwo);
        this.nonIsoCompliantErrorAddressDetails = templateInContext.draftObject.beneficiary?.addressLineOne + ' ' 
          + templateInContext.draftObject.beneficiary?.addressLineTwo;
        this.isNonIsoFirstPartyTemplate = !templateInContext.draftObject.isThirdParty;
        if (this.isNonIsoFirstPartyTemplate) {
          this.nonIsoFirstPartyTemplateRecAccountHolder = {
            accountHolderName1: templateInContext.draftObject.beneficiary.namePartOne,
            accountHolderName2: templateInContext.draftObject.beneficiary.namePartTwo,
            accountHolderAddress1: templateInContext.draftObject.beneficiary.addressLineOne,
            accountHolderAddress2: templateInContext.draftObject.beneficiary.addressLineTwo,
            countryCode: templateInContext.draftObject.beneficiary.countryCode,
            countryDescription: templateInContext.draftObject.beneficiary.countryName
          }
        }
      }
    }
  }

  receivingAccountHolderCityStateValidators(isFirstParty:boolean) {    
    return isFirstParty ? [Validators.pattern(isoFieldsAllowedChars)] : [
      Validators.required,
      Validators.pattern(isoFieldsAllowedChars),
    ]
  } 

  ngOnDestroy(): void {
    Object.keys(this.controlNames).forEach((name) => {
      const names = this.controlNames;
      this.parentForm?.removeControl(this.controlNames[name as keyof typeof names]);
    });

    this.destroy$.next();
    this.destroy$.unsubscribe();
  }

  setReceivingAccountNames(selectedPayeeName: string) {
    this.modifiedPayeeNamesList.forEach((payee) => {
      const payeeName = [payee.name1, payee.name2]
        .filter((val) => !!val.trim())
        .join(this.delimiter);
      if (
        payeeName ===
        this.removeSubstringFromEnd(selectedPayeeName, '(Pending)')
      ) {
        this.name1?.setValue(((payee.name1 ?? "") + " " + (payee.name2 ?? "")).trim());
        return;
      }
    });
  }

  resetInternationalFields(country: string): void {
    if(country === 'US') {
      this.townLocationName?.setValue('');
      this.townLocationName?.markAsUntouched();
      this.district?.setValue('');
      this.district?.markAsUntouched();
    }
    } 

  resetValidators(isFirstParty: boolean) {
    if(!this.isInternational) {
      this.parentForm.controls['receivingAccountHolderState'].setValidators(
       this.receivingAccountHolderCityStateValidators(isFirstParty)
       ),
      this.parentForm.controls['receivingAccountHolderCity'].setValidators(
       this.receivingAccountHolderCityStateValidators(isFirstParty)
       ),
      this.parentForm.controls['receivingAccountHolderZip'].setValidators([
        Validators.pattern(zipCodePattern)
      ]);
    } else {
      this.parentForm.controls['receivingAccountHolderState'].setValidators([
        Validators.pattern(isoFieldsAllowedChars)
      ]),
      this.parentForm.controls['receivingAccountHolderZip'].setValidators([
        Validators.pattern(isoFieldsAllowedChars)
      ]);
    }
    this.parentForm.controls[
      'receivingAccountHolderState'
    ].updateValueAndValidity();

        this.parentForm.controls[
      'receivingAccountHolderCity'
    ].updateValueAndValidity();

    this.parentForm.controls[
      'receivingAccountHolderZip'
    ].updateValueAndValidity();
  }

  resetReceivingAccountHolderControls(firstParty: boolean): void {
    if (firstParty) {
      this.name1?.setValue(this.receivingAccountHolder.name);
      this.streetName?.setValue(this.receivingAccountHolder.accountHolderStreetName);
      this.zipCode?.setValue(this.receivingAccountHolder.zipCode);
      this.city?.setValue(this.receivingAccountHolder.receivingBankCity);
      this.state?.setValue(this.receivingAccountHolder.receivingBankState);
      this.country?.setValue(this.receivingAccountHolder.countryCode);
      this.streetNumber?.setValue(this.receivingAccountHolder.accountHolderStreetNumber);
      this.buildingName?.setValue(this.receivingAccountHolder.buildingName);
      this.apartmentSuiteEtc?.setValue(this.receivingAccountHolder.accountHolderApartmentSuiteEtc);
      this.district?.setValue(this.receivingAccountHolder.district);
      this.department?.setValue(this.receivingAccountHolder.department);
      this.floor?.setValue(this.receivingAccountHolder.floor);
      this.townLocationName?.setValue(this.receivingAccountHolder.townLocationName);

      if (this.allowModifiedPayee){
        createControl(this.parentForm, 'payeeName', this.getDefaultPayee(), [Validators.required], undefined, undefined,
          this.isFromEditReview && !this.fromTemplateStartTransaction ? false : true);
        createControl(this.parentForm, 'modifiedPayeeCheckbox', false, undefined, undefined, undefined, true);
        this.payeeName?.enable();
        this.constructFirstPartyPayeeOptions();
      }
    } else {
      this.name1?.setValue('');
      this.name1?.markAsUntouched();
      this.streetNumber?.setValue('');
      this.streetNumber?.markAsUntouched();
      this.streetName?.setValue('');
      this.streetName?.markAsUntouched();
      this.apartmentSuiteEtc?.setValue('');
      this.apartmentSuiteEtc?.markAsUntouched();
      this.country?.setValue('US');
      this.country?.markAsUntouched();
      this.city?.setValue('');
      this.city?.markAsUntouched();
      this.state?.setValue('');
      this.state?.markAsUntouched();
      this.zipCode?.setValue('');
      this.zipCode?.markAsUntouched();
      this.department?.setValue('');
      this.department?.markAsUntouched();
      this.buildingName?.setValue('');
      this.buildingName?.markAsUntouched();
      this.floor?.setValue('');
      this.floor?.markAsUntouched();
      this.payeeName?.disable();
    }
  }

  clearField(ctrl: UntypedFormControl | undefined): void {
    if (ctrl) {
      ctrl.setValue('');
      ctrl.markAsTouched();
    }
  }

  get allowModifiedPayee(): boolean {
    return !this.useSloa;
  }

  get containsMultipleNames() {
    return !!this.modifiedPayeeNamesList[0].name1.trim() && !!this.modifiedPayeeNamesList[0].name2.trim();
  }

  get modifiedPayeeNamesList(): ModifiedPayeeNames[] {
    const containsDefault = this.firstPartyPayees.find(payee => payee.name1 === this.receivingAccountHolder.name);
    if (!containsDefault){
      this.firstPartyPayees.unshift({name1: this.receivingAccountHolder.name, name2: '', pending: false, isPrimary: true});
    }
    return this.firstPartyPayees;
  }

  getUseSloaFirstPartyAddress() {
    if (this.isISOCompliant) {
      this.useSloaFirstPartyAddress = this.getIntermediarySloaReadOnlyAddress(this.internalReceivingAccountHolder.name, this.internalReceivingAccountHolder.accountHolderStreetNumber, this.internalReceivingAccountHolder.accountHolderStreetName, this.internalReceivingAccountHolder.accountHolderApartmentSuiteEtc, this.internalReceivingAccountHolder.department, this.internalReceivingAccountHolder.buildingName, this.internalReceivingAccountHolder.floor, this.internalReceivingAccountHolder.townLocationName, this.internalReceivingAccountHolder.receivingBankCity, this.internalReceivingAccountHolder.district, this.internalReceivingAccountHolder.receivingBankState, this.internalReceivingAccountHolder.zipCode, this.internalReceivingAccountHolder.countryDescription);
    } else {
      this.useSloaFirstPartyAddress = this.getSloaReadOnlyNonCompliantAddress(this.internalReceivingAccountHolderNonISo.accountHolderName1, this.internalReceivingAccountHolderNonISo.accountHolderName2, this.internalReceivingAccountHolderNonISo.accountHolderAddress1, this.internalReceivingAccountHolderNonISo.accountHolderAddress2, this.internalReceivingAccountHolderNonISo.countryDescription, '');
    }
  }

  getIntermediarySloaReadOnlyAddress(name?: string, streetNumber?: string, streetName?: string, apartmentSuiteEtc?: string, department?: string, buildingName?: string, floor?: string, townLocationName?: string, city?: string, district?: string, state?: string, zipCode?: string, country?: string) {
    return this.formatIsoAddressFieldsService.getFieldsValue(name) +
      this.formatIsoAddressFieldsService.getNextLine(name) +
      this.formatIsoAddressFieldsService.getFieldsValue(streetNumber ? streetNumber + " " + streetName : streetName) +
      this.formatIsoAddressFieldsService.getNextLine(streetNumber ? streetNumber + " " + streetName : streetName) +
      this.formatIsoAddressFieldsService.getFieldsValue(apartmentSuiteEtc, department, buildingName, floor) +
      this.formatIsoAddressFieldsService.getFieldsValue(department, buildingName, floor) +
      this.formatIsoAddressFieldsService.getFieldsValue(buildingName, floor) +
      this.formatIsoAddressFieldsService.getFieldsValue(floor) +
      this.formatIsoAddressFieldsService.getNextLine(apartmentSuiteEtc, department, buildingName, floor) +
      this.formatIsoAddressFieldsService.getFieldsValue(townLocationName, city, district, state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(city, district, state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(district, state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(zipCode) +
      this.formatIsoAddressFieldsService.getNextLine(townLocationName, city, district, state, zipCode) +
      this.formatIsoAddressFieldsService.getFieldsValue(country) +
      this.formatIsoAddressFieldsService.getNextLine(country) 
  }

  getSloaReadOnlyNonCompliantAddress(name?: string, nameCount?: string, address?: string, addressCount?: string, country?: string, phone?: string) {
    return this.formatIsoAddressFieldsService.getFieldsValue(name) +
      this.formatIsoAddressFieldsService.getNextLine(name) +
      this.formatIsoAddressFieldsService.getFieldsValue(nameCount) +
      this.formatIsoAddressFieldsService.getNextLine(nameCount) +
      this.formatIsoAddressFieldsService.getFieldsValue(address) +
      this.formatIsoAddressFieldsService.getNextLine(address) +
      this.formatIsoAddressFieldsService.getFieldsValue(addressCount, country) +
      this.formatIsoAddressFieldsService.getFieldsValue(country) +
      this.formatIsoAddressFieldsService.getNextLine(addressCount, country) +
      phone
  }

  get internalReceivingAccountHolder(): ReceivingAccountHolderIso {
    return {
      name: this.useSloa ? this.parentForm?.value?.receivingAccountHolderName : this.name1?.value ?? '',
      accountHolderStreetNumber: this.useSloa ? this.parentForm?.value?.receivingAccountHolderStreetNumber : this.streetNumber?.value ?? '',
      accountHolderStreetName: this.useSloa ? this.parentForm?.value?.receivingAccountHolderStreetName : this.streetName?.value ?? '',
      accountHolderApartmentSuiteEtc: this.useSloa ? this.parentForm?.value?.receivingAccountHolderApartmentSuiteEtc : this.apartmentSuiteEtc?.value ?? '',
      countryCode: this.useSloa ? this.parentForm?.value?.receivingAccountHolderCountry : this.country?.value ?? '',
      countryDescription: CountryDescriptionFromCode(this.useSloa ? this.parentForm?.value?.receivingAccountHolderCountry : this.country?.value, this.countryListDropdown.options),
      receivingBankCity: this.useSloa ? this.parentForm?.value?.receivingAccountHolderCity : this.city?.value ?? '',
      receivingBankState: this.useSloa ? this.parentForm?.value?.receivingAccountHolderState : this.state?.value ?? '',
      zipCode: this.useSloa ? this.parentForm?.value?.receivingAccountHolderZip : this.zipCode?.value ?? '',
      department: this.useSloa ? this.parentForm?.value?.receivingAccountHolderDepartment : this.department?.value ?? '',
      buildingName: this.useSloa ? this.parentForm?.value?.receivingAccountHolderBuildingName : this.buildingName?.value ?? '',
      floor: this.useSloa ? this.parentForm?.value?.receivingAccountHolderFloor : this.floor?.value ?? '',
      townLocationName: this.useSloa ? this.parentForm?.value?.receivingAccountHolderTownLocationName : this.townLocationName?.value ?? '',
      district: this.useSloa ? this.parentForm?.value?.receivingAccountHolderDistrict : this.district?.value ?? ''
    };
  }

  get internalReceivingAccountHolderNonISo(): ReceivingAccountHolder {
    return {
      accountHolderName1: this.useSloa ? this.parentForm?.value?.receivingAccountHolderName1 : this.name2?.value ?? '',
      accountHolderName2: this.useSloa ? this.parentForm?.value?.receivingAccountHolderName2 : this.nameCont?.value ?? '',
      accountHolderAddress1: this.useSloa ? this.parentForm?.value?.receivingAccountHolderAddress1 : this.address?.value ?? '',
      accountHolderAddress2: this.useSloa ? this.parentForm?.value?.receivingAccountHolderAddress2 : this.addressCont?.value ?? '',
      countryCode: this.useSloa ? this.parentForm?.value?.receivingAccountHolderCountry : this.country?.value ?? '',
      countryDescription: CountryDescriptionFromCode(this.useSloa ? this.parentForm?.value?.receivingAccountHolderCountry : this.country?.value, this.countryListDropdown.options),
    };
  }

  isClosedAddressFields($event: boolean) {
    if ($event == false) {
      this.hideAddressFields = "yes";
      this.department?.reset();
      this.buildingName?.reset();
      this.floor?.reset();
    } else {
      this.hideAddressFields = "no";
    }
  }

  onBlurFieldsTrimWhiteSpace(controlName: string) {
    const selectedControl = this.parentForm?.get(this.controlNames[controlName as keyof typeof this.controlNames]);
    selectedControl?.setValue(selectedControl?.value?.trim());
  }

  get isInternational(): boolean {
    return this.useSloa ? this.isInternationalForUseSloa?.trim() !== 'US'  : this.parentForm?.controls?.receivingAccountHolderCountry?.value?.trim() !== 'US';
  }

  get payeeName(): UntypedFormControl {
    return this.parentForm.controls.payeeName as UntypedFormControl;
  }

  get name1(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.name1) as UntypedFormControl;
  }

  get name2(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.name2) as UntypedFormControl;
  }

  get nameCont(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.nameCont) as UntypedFormControl;
  }

  get address(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.address) as UntypedFormControl;
  }

  get addressCont(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.addressCont) as UntypedFormControl;
  }

  get streetNumber(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.streetNumber) as UntypedFormControl;
  }

  get streetName(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.streetName) as UntypedFormControl;
  }

  get apartmentSuiteEtc(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.apartmentSuiteEtc) as UntypedFormControl;
  }

  get country(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.country) as UntypedFormControl;
  }

  get city(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.receivingBankCity) as UntypedFormControl;
  }

  get state(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.receivingBankState) as UntypedFormControl;
  }

  get zipCode(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.zipCode) as UntypedFormControl;
  }

  get department(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.department) as UntypedFormControl;
  }

  get buildingName(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.buildingName) as UntypedFormControl;
  }

  get floor(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.floor) as UntypedFormControl;
  }

  get townLocationName(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.townLocationName) as UntypedFormControl;
  }

  get district(): UntypedFormControl | undefined {
    return this.parentForm?.get(this.controlNames.district) as UntypedFormControl;
  }
  
  get name1Error(): string | undefined {
    const error = this.mapErrors(this.name1IsoModel.label, this.name1, 140);
    return error === undefined ? '' : error;
  }

  get taxpayerNameError(): string | undefined {
    const errorMap = taxPayerNameErrorMap(140);
    const errorKeys = Object.keys(this.name1?.errors ?? {});
    if (errorKeys.length > 0) {
      return errorMap[errorKeys[0] as keyof typeof errorMap];
    }
    return '';
  }

  get name2Error(): string | undefined {
    const error = this.mapErrors(this.name2IsoModel.label, this.name2, 140);
    return error === undefined ? '' : error;
  }

  get nameContError(): string | undefined {
    const error = this.mapErrors(this.nameContModel.label, this.nameCont, 140);
    return error === undefined ? '' : error;
  }

  get addressError(): string | undefined {
    const error = this.mapErrors(this.addressModel.label, this.address, 140);
    return error === undefined ? '' : error;
  }

  get addressContError(): string | undefined {
    const error = this.mapErrors(this.addressContModel.label, this.addressCont, 140);
    return error === undefined ? '' : error;
  }

  get streetNumberError(): string | undefined {
    const error = this.mapErrors(this.streetNumberModel.label, this.streetNumber, this.maxLength16);
    return error === undefined ? '' : error;
  }

  get streetNameError(): string | undefined {
    const error = this.mapErrors(this.streetNameModel.label, this.streetName, this.maxLength70);
    return error === undefined ? '' : error;
  }

  get apartmentSuiteError(): string | undefined {
    const error = this.mapErrors(this.apartmentSuiteModel.label, this.apartmentSuiteEtc, this.maxLength70);
    return error === undefined ? '' : error;
  }

  get cityError(): string | undefined {
    const error = this.mapErrors(this.cityModel.label, this.city, this.maxLength35);
    return error === undefined ? '' : error;
  }

  get stateError(): string | undefined {
    const error = this.mapErrors(this.stateModel.label, this.state, this.maxLength35);
    return error === undefined ? '' : error;
  }

  get zipError(): string | undefined {
    const errorMap = isoInputZipErrorMap('Zip code', this.maxLength16);
    const errorKeys = Object.keys(this.zipCode?.errors ?? {});

    if (errorKeys.length > 0) {
      return errorMap[errorKeys[0] as keyof typeof isoInputZipErrorMap];
    }

    return '';
  }
  get intlZipError(): string | undefined {
    const error = this.mapErrors(this.intlZipModel.label, this.zipCode, this.maxLength16);
    return error === undefined ? '' : error;
  }

  get departmentError(): string | undefined {
    const error = this.mapErrors(this.departmentModel.label, this.department, this.maxLength70);
    return error === undefined ? '' : error;
  }

  get buildingNameError(): string | undefined {
    const error = this.mapErrors(this.buildingNameModel.label, this.buildingName, this.maxLength35);
    return error === undefined ? '' : error;
  }

  get floorError(): string | undefined {
    const error = this.mapErrors(this.floorModel.label, this.floor, this.maxLength70);
    return error === undefined ? '' : error;
  }

  get townLocationNameError(): string | undefined {
    const error = this.mapErrors(this.townLocationNameModel.label, this.townLocationName, this.maxLength35);
    return error === undefined ? '' : error;
  }

  get districtError(): string | undefined {
    const error = this.mapErrors(this.districtModel.label, this.district, this.maxLength35);
    return error === undefined ? '' : error;
  }

  private mapErrors(
    name: string,
    control: UntypedFormControl | undefined,
    maxLength: number
  ): string | undefined {
    const errorMap = isoInputFieldErrorMap(name, maxLength);
    const errorKeys = Object.keys(control?.errors ?? {});

    if (errorKeys.length > 0) {
      return errorMap[errorKeys[0] as keyof typeof isoInputFieldErrorMap];
    }

    return '';
  }


  getDefaultPayee(): string {
    if (!this.fromTemplateStartTransaction){
      return [this.modifiedPayeeNamesList[0].name1, this.modifiedPayeeNamesList[0].name2].filter(val => !!val.trim()).join(this.delimiter);
    }

    const templateInContext = this.templates.find(template => template.draftId === this.templateId);
    const payeeInfoForTemplate = this.modifiedPayeeNamesList.find(payee => payee.name1 === templateInContext?.draftObject.beneficiary.namePartOne && payee.name2 === templateInContext?.draftObject.beneficiary.namePartTwo);
    if (payeeInfoForTemplate){
      let payeeName = [payeeInfoForTemplate.name1, payeeInfoForTemplate.name2].join(this.delimiter).trim();
      let payeeStatusSuffix = payeeInfoForTemplate.pending ? ' (Pending)' : '';
      return `${payeeName}${payeeStatusSuffix}`;
    } else {
      return [this.modifiedPayeeNamesList[0].name1, this.modifiedPayeeNamesList[0].name2].filter(val => !!val.trim()).join(this.delimiter);
    }
  }

  constructFirstPartyPayeeOptions(): void {
    const options = this.modifiedPayeeNamesList.map(payee => {
      const payeeName = [payee.name1, payee.name2].filter(val => !!val.trim()).join(this.delimiter);
      const payeeStatusSuffix = payee.pending ? ' (Pending)' : '';
      const payeeDisplayName = `${payeeName}${payeeStatusSuffix}`;
      return new OptionDropdown(payeeDisplayName, payeeDisplayName);
    });
    this.firstPartyPayeesSelectOptions.options = options;
  }

  removeSubstringFromEnd(str: string, subStr: string): string {
    const escapedSubStr = subStr.replace(/[-/\\^$*+?.()|[\]{}]/g, '\\$&');
    const regPattern = new RegExp(`${escapedSubStr}`);

    return str.replace(regPattern, '').trim();
  }

  getWireAccountNumber($event: string) {
    this.wireAccountNumber = $event;
  }
  showWarningBanner() {
    return this.isFromCreateTransaction ? this.parentForm?.controls?.aba?.value == this.wireAccountNumber && !this.isIrsAba :
                                          this.parentForm?.controls?.aba?.value == this.wireAccountNumber;
  }

  showAddressWarningBanner(): boolean {
    return (!this.useSloa && (this.parentForm?.controls?.receivingAccountHolderCity?.value?.trim() ||
      this.parentForm?.controls?.receivingAccountHolderState?.value?.trim()) &&
      (!this.parentForm?.controls?.receivingAccountHolderStreetName?.value?.trim() ||
        !this.parentForm?.controls?.receivingAccountHolderStreetNumber?.value?.trim()))
  }
}
